import { Box, Grid } from '@mui/material';
import { useHistory } from 'react-router-dom';
import { useMemo } from 'react';

import { SetupCard, BusinessSetupCardsProps } from './BusinessSetupCards.interface';

import HighlightCard from 'modules/common/components/HighlightCard';
import useMenuAreas from 'modules/common/components/Layout/Sidebar/useMenuAreas';
import PayrollImage from 'assets/img/business-setup-cards/payroll.png';
import AccountingImage from 'assets/img/business-setup-cards/accounting.png';
import InvoicingImage from 'assets/img/business-setup-cards/invoicing.png';

const setupCardsConfig: Omit<SetupCard, 'isLocked'>[] = [
    {
        id: 'payroll',
        title: 'Payroll',
        description: 'Running payroll is a key part of operating like an S Corp.',
        image: PayrollImage,
        route: '/payroll',
    },
    {
        id: 'accounting',
        title: 'Accounting',
        description: 'Manage accounting for accurate bookkeeping.',
        image: AccountingImage,
        route: '/bookkeeping',
    },
    {
        id: 'invoicing',
        title: 'Invoicing',
        description: 'Bill your customers and get paid quickly.',
        image: InvoicingImage,
        route: '/bookkeeping/invoicing',
    },
];

export default function BusinessSetupCards({ variant = 'default' }: BusinessSetupCardsProps) {
    const history = useHistory();
    const menuAreas = useMenuAreas();

    // Determine lock status based on menu areas and filter out cards that aren't in menuAreas
    const setupCards = useMemo<SetupCard[]>(() => {
        return setupCardsConfig
            .map((card) => {
                // Find matching menu area by checking if the route matches the path or basePath
                // Todo: asaiyed@collective.com | Add an enum menuId to MenuItem and use it here instead of route
                const menuArea = menuAreas.find((area) => [area.path, area.basePath].includes(card.route));

                // Only include cards that exist in menuAreas (respects custom validation)
                if (!menuArea) {
                    return null;
                }

                return {
                    ...card,
                    isLocked: menuArea.locked ?? false,
                };
            })
            .filter((card): card is SetupCard => card !== null);
    }, [menuAreas]);

    const handleStartClick = (route: string) => {
        history.push(route);
    };

    const isVariantElevated = variant === 'elevated';

    return (
        <Box
            mt={3}
            mb={isVariantElevated ? 3 : 0}
            sx={{
                alignSelf: isVariantElevated ? 'flex-start' : 'center',
                maxWidth: '900px',
            }}>
            <Grid container spacing={3}>
                {setupCards.map((card) => (
                    <Grid key={card.id} size={{ xs: 12, sm: 4, md: 4 }}>
                        <HighlightCard
                            title={card.title}
                            description={card.description}
                            imageSrc={card.image}
                            actionLabel="Access"
                            onAction={() => handleStartClick(card.route)}
                            isLocked={card.isLocked}
                            sx={{
                                borderColor: isVariantElevated ? 'cement.300' : 'transparent',
                            }}
                        />
                    </Grid>
                ))}
            </Grid>
        </Box>
    );
}
